import { useState, useRef, useEffect } from "react";

interface UseAudioVisualizationProps {
	audioElement: HTMLAudioElement | null;
	isPlaying: boolean;
	barCount?: number;
	/** If true, shows visualization even when audio is muted (requires audio element to be playing) */
	visualizeWhenMuted?: boolean;
	/** If true, shows simulated idle visualization (for when autoplay is blocked) */
	isAutoplayBlocked?: boolean;
}

/**
 * Hook for visualizing audio with Web Audio API
 * Creates audio context once, reuses for lifetime
 * Avoids "already connected" errors by lazy initialization
 * Can visualize even when audio is muted if visualizeWhenMuted is true
 * Shows simulated idle animation when autoplay is blocked
 */
export function useAudioVisualization({
	audioElement,
	isPlaying,
	barCount = 28,
	visualizeWhenMuted = false,
	isAutoplayBlocked = false,
}: UseAudioVisualizationProps) {
	const [audioLevels, setAudioLevels] = useState<number[]>(
		Array(barCount).fill(0),
	);
	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const historyRef = useRef<number[]>([]);
	const lastUpdateRef = useRef<number>(0);

	// Initialize audio context and analyser ONCE when audio element is available
	useEffect(() => {
		if (!audioElement || audioContextRef.current) return;

		try {
			const audioContext = new (
				window.AudioContext || (window as any).webkitAudioContext
			)();
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 512;
			analyser.smoothingTimeConstant = 0.3;

			// Create source and connect pipeline
			const source = audioContext.createMediaElementSource(audioElement);
			const gainNode = audioContext.createGain();
			gainNode.gain.value = 3.0; // Boost for better visualization

			source.connect(gainNode);
			gainNode.connect(analyser);
			analyser.connect(audioContext.destination);

			audioContextRef.current = audioContext;
			analyserRef.current = analyser;
			sourceRef.current = source;
		} catch (error) {
			console.error("[AudioVisualization] Failed to create audio context:", error);
		}

		return () => {
			// Cleanup only on unmount
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
			}
		};
	}, [audioElement]);

	// Simulated visualization when autoplay is blocked (no audio element playing)
	useEffect(() => {
		if (!isAutoplayBlocked) return;

		// Gentle idle animation while waiting for user interaction
		const updateIdleVisualization = () => {
			const idleLevels = Array(barCount).fill(0).map(() => {
				// Generate gentle random variation (0.1-0.4 range)
				return 0.1 + Math.random() * 0.3;
			});
			setAudioLevels(idleLevels);
		};

		// Update every 100ms for smooth but subtle animation
		const interval = setInterval(updateIdleVisualization, 100);
		updateIdleVisualization(); // Initial update

		return () => {
			clearInterval(interval);
		};
	}, [isAutoplayBlocked, barCount]);

	// Start/stop visualization based on playing state
	useEffect(() => {
		// Don't run real visualization if we're showing simulated idle animation
		if (isAutoplayBlocked) return;
		if (!analyserRef.current || !audioElement) return;

		// Show visualization if playing OR if muted but visualizeWhenMuted is enabled
		const shouldVisualize = isPlaying || (visualizeWhenMuted && !audioElement.paused);

		if (shouldVisualize) {
			const bufferLength = analyserRef.current.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);

			const updateLevels = (timestamp: number) => {
				if (!analyserRef.current) return;

				const timeSinceLastUpdate = timestamp - lastUpdateRef.current;
				if (timeSinceLastUpdate >= 10) {
					lastUpdateRef.current = timestamp;

					analyserRef.current.getByteFrequencyData(dataArray);

					const usefulBinStart = 2;
					const usefulBinEnd = Math.floor(bufferLength * 0.4);
					let sum = 0;
					let count = 0;

					for (let i = usefulBinStart; i < usefulBinEnd; i += 3) {
						sum += dataArray[i];
						count++;
					}

					const avgLevel = sum / count;
					let normalized = Math.pow(avgLevel / 255, 2.5) * 1.5;

					if (normalized > 0.3) {
						const excess = normalized - 0.3;
						normalized = 0.3 + excess * 1.6;
					}
					normalized = Math.min(1, normalized);

					historyRef.current.push(normalized);
					if (historyRef.current.length > barCount) {
						historyRef.current.shift();
					}

					const displayLevels = [...historyRef.current];
					while (displayLevels.length < barCount) {
						displayLevels.unshift(0);
					}

					setAudioLevels(displayLevels);
				}

				animationFrameRef.current = requestAnimationFrame(updateLevels);
			};

			animationFrameRef.current = requestAnimationFrame(updateLevels);
		} else {
			// Stop animation when paused
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			historyRef.current = [];
			setAudioLevels(Array(barCount).fill(0));
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
		};
	}, [isPlaying, barCount, visualizeWhenMuted, audioElement, isAutoplayBlocked]);

	return audioLevels;
}
